import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import {
  FormulaireFournisseur,
  type Valeurs,
} from "@/composants/formulaire-fournisseur";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Erreur } from "@/composants/ui/etats";
import { messageDe } from "@/lib/chargement";
import {
  api,
  type PropositionAnnuaire,
  type RessemblanceFournisseur,
} from "@/lib/api";
import { useSession } from "@/session";

/**
 * Créer une fiche fournisseur, assistée par l'annuaire des entreprises (#13).
 *
 * L'annuaire propose, l'utilisateur corrige puis crée : une proposition
 * n'écrit rien. S'il ne répond pas, la fiche se saisit à la main — le
 * formulaire reste ouvert en toutes circonstances.
 *
 * Avant de créer, LABGEST cherche les fiches qui ressemblent (nom proche, même
 * domaine d'adresse électronique). Il avertit sans bloquer : le bouton devient
 * « Créer quand même », et une saisie changée depuis l'avertissement est
 * vérifiée de nouveau.
 */
export function PageFournisseurNouveau() {
  const navigate = useNavigate();
  const session = useSession();
  const voitLesNotesConfidentielles = session.peut("CONSULTER_NOTES_CONFIDENTIELLES_FOURNISSEURS");
  const [refus, setRefus] = useState<string | null>(null);
  const [preremplie, setPreremplie] = useState<{
    generation: number;
    valeurs: Partial<Valeurs>;
  }>({
    generation: 0,
    valeurs: {},
  });
  const [avertissement, setAvertissement] = useState<{
    saisie: string;
    ressemblances: RessemblanceFournisseur[];
  } | null>(null);

  const creer = async (champs: Record<string, unknown>) => {
    setRefus(null);
    try {
      const saisie = JSON.stringify(champs);
      if (avertissement?.saisie !== saisie) {
        const { ressemblances } = await api.ressemblancesFournisseur({
          nom: (champs.nom as string | null) ?? undefined,
          email: (champs.email as string | null) ?? undefined,
        });
        if (ressemblances.length > 0) {
          setAvertissement({ saisie, ressemblances });
          return;
        }
      }
      const fiche = await api.creerFournisseur(champs);
      navigate(`/fournisseurs/${fiche.id}`);
    } catch (cause) {
      setRefus(messageDe(cause));
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/fournisseurs"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Tous les fournisseurs
      </Link>
      <h1 className="text-xl font-semibold text-encre">Nouveau fournisseur</h1>
      <RechercheAnnuaire
        onReprendre={(proposition) => {
          setAvertissement(null);
          setPreremplie({
            generation: preremplie.generation + 1,
            valeurs: {
              nom: proposition.nom,
              siren: proposition.siren,
              siret: proposition.siret ?? "",
              adresse_ligne1: proposition.adresse_ligne1 ?? "",
              code_postal: proposition.code_postal ?? "",
              ville: proposition.ville ?? "",
            },
          });
        }}
      />
      {refus ? <Erreur message={refus} /> : null}
      <Carte>
        <CorpsCarte>
          {preremplie.generation > 0 ? (
            <p role="status" className="mb-4 text-sm text-encre-douce">
              Fiche préremplie depuis l'annuaire des entreprises : vérifiez-la,
              corrigez-la au besoin, puis créez-la.
            </p>
          ) : null}
          <FormulaireFournisseur
            // Une nouvelle proposition remonte le formulaire avec ses valeurs.
            key={preremplie.generation}
            initiales={preremplie.valeurs}
            avecNotesConfidentielles={voitLesNotesConfidentielles}
            libelleValider={
              avertissement ? "Créer quand même" : "Créer la fiche"
            }
            mention={
              avertissement ? (
                <Ressemblances liste={avertissement.ressemblances} />
              ) : null
            }
            onAnnuler={() => navigate("/fournisseurs")}
            onValider={creer}
          />
        </CorpsCarte>
      </Carte>
    </div>
  );
}

function RechercheAnnuaire({
  onReprendre,
}: {
  onReprendre: (proposition: PropositionAnnuaire) => void;
}) {
  const [saisie, setSaisie] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [propositions, setPropositions] = useState<
    PropositionAnnuaire[] | null
  >(null);
  const [indisponible, setIndisponible] = useState<string | null>(null);

  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">
          Chercher dans l'annuaire des entreprises
        </h2>
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            setEnCours(true);
            setIndisponible(null);
            setPropositions(null);
            try {
              setPropositions(
                (await api.annuaireEntreprises(saisie.trim())).propositions,
              );
            } catch (cause) {
              setIndisponible(messageDe(cause));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <div className="min-w-0 flex-1 basis-56">
            <Champ
              libelle="SIREN, SIRET ou nom"
              minLength={3}
              required
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
            />
          </div>
          <Bouton type="submit" disabled={enCours}>
            <Search className="h-4 w-4" aria-hidden />
            {enCours ? "Recherche…" : "Chercher"}
          </Bouton>
        </form>
        <p className="text-xs text-encre-douce">
          LABGEST propose une fiche préremplie ; rien n'est créé avant que vous
          la validiez. La fiche se saisit aussi à la main, plus bas.
        </p>
        {indisponible ? (
          <p role="status" className="text-sm text-encre">
            {indisponible}
          </p>
        ) : null}
        {propositions && propositions.length === 0 ? (
          <p role="status" className="text-sm text-encre-douce">
            Aucune entreprise ne répond à cette recherche : saisissez la fiche à
            la main.
          </p>
        ) : null}
        {propositions && propositions.length > 0 ? (
          <ul className="flex flex-col divide-y divide-filet">
            {propositions.map((p) => (
              <li
                key={`${p.siren}-${p.siret ?? ""}`}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium break-words text-encre">
                    {p.nom}
                  </p>
                  <p className="text-xs text-encre-douce">
                    SIRET {p.siret ?? "inconnu"}
                    {p.ville ? ` · ${p.code_postal ?? ""} ${p.ville}` : ""}
                  </p>
                </div>
                <Bouton
                  type="button"
                  aria-label={`Reprendre ${p.nom}, SIRET ${p.siret ?? "inconnu"}`}
                  onClick={() => onReprendre(p)}
                >
                  Reprendre
                </Bouton>
              </li>
            ))}
          </ul>
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}

function Ressemblances({ liste }: { liste: RessemblanceFournisseur[] }) {
  return (
    <div
      role="status"
      className="rounded-[6px] border border-laiton/40 bg-chene-clair/30 px-4 py-3"
    >
      <p className="text-sm font-medium text-encre">
        Attention :{" "}
        {liste.length === 1 ? "une fiche ressemble" : "des fiches ressemblent"}{" "}
        à celle-ci.
      </p>
      <ul className="mt-2 flex flex-col gap-1 text-sm text-encre">
        {liste.map((r) => (
          <li key={r.id}>
            <Link
              to={`/fournisseurs/${r.id}`}
              className="underline underline-offset-4"
            >
              {r.nom}
            </Link>{" "}
            — {r.motifs.join(", ")}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-sm text-encre-douce">
        S'il s'agit bien d'un autre fournisseur, créez la fiche quand même.
      </p>
    </div>
  );
}
